export type NodeKind = string;

export type NodeCategory =
  | "source"
  | "frame"
  | "inference"
  | "logic"
  | "output"
  | "analytics";

export type NodeCapability = "live" | "planned";

export type NodeDefinition = {
  kind: NodeKind;
  label: string;
  category: NodeCategory;
  subtitle: string;
  detail: string;
  input: string;
  output: string;
  capability: NodeCapability;
  hidden?: boolean;
  inputPorts?: string[];
  outputPorts?: GroupPort[];
  outputProperties?: PropertyDefinition[];
};

export type PropertyDefinition = {
  key: string;
  label: string;
  value: string;
};

export type GroupPort = {
  id: string;
  label: string;
};

export type GroupGraph = {
  inputs: GroupPort[];
  outputs: GroupPort[];
  nodes: WorkflowNode[];
  edges: Edge[];
};

export type WorkflowNode = NodeDefinition & {
  id: string;
  title: string;
  x: number;
  y: number;
  width: number;
  height: number;
  status: string;
  config: Record<string, string>;
  properties: Record<string, string>;
  group?: GroupGraph;
};

export type Edge = {
  source: string;
  sourcePort?: string;
  target: string;
  targetPort?: string;
};

export type SettingsField = {
  key: string;
  label: string;
  placeholder: string;
  type?: "text" | "number" | "url" | "password" | "select" | "multi-select";
  options?: string[];
  defaultValue?: string;
};

export type WorkflowNodeCatalog = {
  nodes: Array<NodeDefinition & { settings: SettingsField[] }>;
};

export let definitions: NodeDefinition[] = [];
export let definitionByKind: Record<NodeKind, NodeDefinition> = {};
export let settingsFields: Record<NodeKind, SettingsField[]> = {};

export function installNodeCatalog(catalog: WorkflowNodeCatalog) {
  definitions = catalog.nodes.map((node) => ({
    kind: node.kind,
    label: node.label,
    category: node.category,
    subtitle: node.subtitle,
    detail: node.detail,
    input: node.input,
    output: node.output,
    capability: node.capability,
    hidden: node.hidden,
    inputPorts: node.inputPorts,
    outputPorts: node.outputPorts,
    outputProperties: node.outputProperties,
  }));
  definitionByKind = Object.fromEntries(
    definitions.map((definition) => [definition.kind, definition]),
  );
  settingsFields = Object.fromEntries(
    catalog.nodes.map((definition) => [definition.kind, definition.settings]),
  );
}

export function createGroupGraph(inputCount = 2, outputCount = 2): GroupGraph {
  return {
    inputs: Array.from({ length: inputCount }, (_, index) => ({
      id: `group-input-${index + 1}`,
      label: `Input ${index + 1}`,
    })),
    outputs: Array.from({ length: outputCount }, (_, index) => ({
      id: `group-output-${index + 1}`,
      label: `Output ${index + 1}`,
    })),
    nodes: [],
    edges: [],
  };
}

export function createNode(
  kind: NodeKind,
  id: string,
  x: number,
  y: number,
): WorkflowNode {
  const definition = definitionByKind[kind];
  if (!definition) throw new Error(`Unknown workflow node kind: ${kind}`);
  const group = kind === "group" ? createGroupGraph() : undefined;
  return {
    ...definition,
    inputPorts: group?.inputs.map((port) => port.id) ?? definition.inputPorts,
    id,
    title: definition.label,
    x,
    y,
    width: 216,
    height: 144,
    status: kind === "camera" ? "Streaming" : "Ready",
    config: group
      ? {
          inputs: String(group.inputs.length),
          outputs: String(group.outputs.length),
        }
      : {},
    properties: Object.fromEntries(
      (
        definition.outputProperties ?? [
          { key: "output", label: "Output", value: definition.output },
        ]
      ).map((property) => [property.key, property.value]),
    ),
    group,
  };
}
